import React, { useState, useEffect, useRef } from 'react';
import { Link, NavLink } from 'react-router-dom';
import { useCandidateAuth } from '../../../contexts/CandidateAuthContext';

const Header = ({ onMenuToggle }) => {
  const { user, logout, isAuthenticated } = useCandidateAuth();
  const [userData, setUserData] = useState(user);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Listen for user data updates from local storage
  useEffect(() => {
    // Load initial user data from local storage
    const loadUserDataFromStorage = () => {
      const userStr = localStorage.getItem('candidate_user');
      if (userStr) {
        try {
          const parsedUser = JSON.parse(userStr);
          setUserData(parsedUser);
        } catch (error) {
          console.error('Error parsing user data from local storage:', error);
        }
      }
    };
    
    // Load initial data
    loadUserDataFromStorage();
    
    // Listen for custom event when user data is updated
    const handleUserDataUpdated = (event) => {
      if (event.detail && event.detail.userData) {
        setUserData(event.detail.userData);
      }
    };
    
    // Listen for storage changes (in case other tabs update it)
    const handleStorageChange = (e) => {
      if (e.key === 'candidate_user') {
        loadUserDataFromStorage();
      }
    };
    
    // Add event listeners
    window.addEventListener('userDataUpdated', handleUserDataUpdated);
    window.addEventListener('storage', handleStorageChange);
    
    // Cleanup
    return () => {
      window.removeEventListener('userDataUpdated', handleUserDataUpdated);
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  // Also sync with context user if it changes
  useEffect(() => {
    if (user) {
      setUserData(user);
    }
  }, [user]);

  // Logout function
  const handleLogout = () => {
    logout();
  };  

  const [showUserMenu, setShowUserMenu] = useState(false);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowUserMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleMenuItemClick = () => {
    setShowUserMenu(false);
  };

  const handleUserMenuToggle = () => {
    setShowUserMenu(!showUserMenu);
  };

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  // Use userData for display, fallback to context user
  const displayUser = userData || user;

  // Get the first letter for avatar fallback
  const getInitial = () => {
    if (!displayUser) return 'C';
    return (
      displayUser.firstName?.charAt(0)?.toUpperCase() ||
      displayUser.name?.charAt(0)?.toUpperCase() ||
      'C'
    );
  };

  // Navigation items from Sidebar.js
  const navItems = [
    { path: '/candidate/dashboard', icon: 'fas fa-tachometer-alt', label: 'Dashboard' },
    { path: '/candidate/jobs', icon: 'fas fa-briefcase', label: 'Find Jobs' },
    { path: '/candidate/saved-jobs', icon: 'fas fa-heart', label: 'Saved Jobs' },
    { path: '/candidate/profile', icon: 'fas fa-user', label: 'My Profile' },
  ];

  return (
    <header className="navbar bg-white shadow-sm z-10 sticky top-0">
      <div className="flex items-center justify-between px-4 py-3">
        <div className="flex items-center">
          {/* Mobile menu button - replaces the sidebar toggle */}
          <button 
            onClick={toggleMobileMenu}
            className="md:hidden text-gray-400 focus:outline-none mr-4"
          >
            <i className={`fas ${mobileMenuOpen ? 'fa-times' : 'fa-bars'} text-xl`}></i>
          </button>
          
          {/* Logo - Hidden on mobile (only shows in mobile menu) */}
          <div className="items-center">
            <Link to="/candidate/dashboard" className="flex items-center">
              <img 
                  src="/logo-light.png" 
                  alt="Hunar Pool" 
                  className="h-8 w-auto" 
                  draggable="false"
              />
            </Link>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex ml-6 space-x-2">
            {navItems.map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                className={({ isActive }) =>
                  `flex items-center px-4 py-3 text-gray-100 rounded-lg hover:bg-gray-100 hover:text-primary transition-colors ${
                    isActive ? 'active-nav' : ''
                  }`
                }
              >
                <i className={`${item.icon} mr-3`}></i>
                <span>{item.label}</span>
              </NavLink>
            ))}
          </nav>
        </div>
        
        <div className="flex items-center space-x-4">          
          {/* User Menu - EXACTLY AS BEFORE, NO CHANGES */}
          <div className="relative" ref={dropdownRef}>
            <div className="flex items-center space-x-4">
              {isAuthenticated && displayUser && (
                <>
                  {/* <Link 
                    to="/candidate/profile" 
                    className="flex items-center space-x-3"
                  >
                    {user.avatar && (
                      <img 
                        src={displayUser.avatar} 
                        alt={displayUser.name}
                        className="w-8 h-8 rounded-full"
                      />
                    )}
                    <span className="text-sm font-medium text-white">
                      {displayUser.fullName}
                    </span>
                  </Link> */}  

                  <button 
                    onClick={handleUserMenuToggle}
                    className="flex items-center text-sm focus:outline-none"
                  >
                    {(user.avatar ? (
                      <img 
                        src={displayUser.avatar} 
                        alt={displayUser.name}
                        className="w-8 h-8 rounded-full"
                      />
                    ) :  
                      <div className="h-8 w-8 rounded-full bg-gradient-to-r from-[#0096C7] to-[#4361EE] hover:from-[#00a8e0] hover:to-[#4a6bf5] flex items-center justify-center text-white font-semibold">
                         {getInitial()}
                      </div>
                    )}  
                      <span className="ml-2 text-gray-100 font-medium hidden md:block">{displayUser.fullName}</span>
                      <i className="fas fa-chevron-down ml-1 text-gray-100 text-xs"></i>
                  </button>
                  
                  {/* <button
                    onClick={handleLogout}
                    className="ml-4 px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-colors duration-200"
                  >
                    Logout
                  </button> */}
                </>              
              )}
            </div>
     
            {showUserMenu && (
              <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg py-1 z-50">
                <Link 
                  to="/candidate/profile" 
                  onClick={handleMenuItemClick}
                  className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                >
                  <i className="fas fa-user mr-2"></i>My Profile
                </Link>
                {/* <Link 
                  to="/candidate/settings" 
                  onClick={handleMenuItemClick}
                  className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                >
                  <i className="fas fa-cog mr-2"></i>Settings
                </Link>
                <Link 
                  to="/candidate/change-password" 
                  onClick={handleMenuItemClick}
                  className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                >
                  <i className="fas fa-lock mr-2"></i>Change Password
                </Link> */}
                <div className="border-t border-gray-100"></div>
                <button 
                  onClick={handleLogout}
                  className="block w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                >
                  <i className="fas fa-sign-out-alt mr-2"></i>Sign Out
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Mobile Overlay - with transition */}
      <div 
        className={`fixed inset-0 bg-black md:hidden z-20 transition-opacity duration-300 ease-in-out ${
          mobileMenuOpen ? 'opacity-50' : 'opacity-0 pointer-events-none'
        }`}
        onClick={() => setMobileMenuOpen(false)}
      ></div>
      
      {/* Mobile Menu - always in DOM, hidden with translate */}
      <div className={`sidebar fixed md:hidden bg-white w-64 min-h-screen shadow-md z-30 top-0 left-0 transition-transform duration-300 ease-in-out ${
        mobileMenuOpen ? 'translate-x-0' : '-translate-x-full'
      }`}>
        <nav className="mt-4 px-4">
          <div className="flex items-center justify-between py-4">
            <img 
              src="/logo-light.png" 
              alt="Hunar Pool" 
              className="h-8 w-auto" 
              draggable="false"
            />
            <button 
              onClick={() => setMobileMenuOpen(false)}
              className="text-gray-400 hover:text-gray-600"
            >
              <i className="fas fa-times text-xl"></i>
            </button>
          </div>
          <hr/>

          <ul className="space-y-2 mt-4">
            {navItems.map((item) => (
              <li key={item.path}>
                <NavLink
                  to={item.path}
                  onClick={() => setMobileMenuOpen(false)}
                  className={({ isActive }) =>
                    `flex items-center px-4 py-3 text-gray-100 rounded-lg hover:bg-gray-100 hover:text-primary transition-colors ${
                      isActive ? 'active-nav' : ''
                    }`
                  }
                >
                  <i className={`${item.icon} mr-3`}></i>
                  <span>{item.label}</span>
                </NavLink>
              </li>
            ))}
          </ul>
        </nav>
      </div>
    </header>
  );
};

export default Header;